import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import withJobActions from './withJobActions';
import JobContractRequirementsItem from '../../../admin/jobs/jobContractRequirementsItem';
import entityComposition from '../../../common/classes/entityComposition';
const JobContractRequirementsSubsection = props => {
  const {
    onJobRemove,
    onJobAdd,
    onJobChange,
    active
  } = props;
  const handleRemove = useCallback((index, path, removeIndex) => onJobRemove(`contractRequirements/${index}/${path}`, removeIndex), [onJobRemove]);
  const handleAdd = useCallback((index, path, defaultData) => onJobAdd(`contractRequirements/${index}/${path}`, defaultData), [onJobAdd]);
  /**
   * we need to capture the handleChange because before we have actually added any
   * contract requirements we use some default data but we don't actually add it
   * to the job model until the user tries to change/modify it the first time.
   *
   * If we already have data, we just pass on the handleChange to
   * props.onChange function.
   */
  const handleChange = useCallback((path, value) => {
    if (!active.contractRequirements || active.contractRequirements.length === 0) {
      // need to add it because we used a temporary one when no data was available.
      const defaultData = {
        jobID: active.id,
        surfaceProfileUnits: 0
      };
      const event = path[0];
      defaultData[event.name] = event.value;
      setTimeout(() => {
        onJobAdd('contractRequirements', defaultData);
      }, 0);
    } else {
      onJobChange(`contractRequirements/${path}`, value);
    }
  }, [active.contractRequirements, active.id, onJobAdd, onJobChange]);
  const {
    astmActions,
    astm,
    customRequirementActions,
    customRequirement,
    ISO8501Actions,
    ISO8501,
    naceActions,
    nace,
    milProfileMeansActions,
    milProfileMeans,
    sspcActions,
    sspc
  } = props;
  useEffect(() => {
    astmActions.serverIndex(astm.indexQuery);
    customRequirementActions.serverIndex(customRequirement.indexQuery);
    ISO8501Actions.serverIndex(ISO8501.indexQuery);
    naceActions.serverIndex(nace.indexQuery);
    milProfileMeansActions.serverIndex(milProfileMeans.indexQuery);
    sspcActions.serverIndex(sspc.indexQuery);
  }, [ISO8501.indexQuery, ISO8501Actions, astm.indexQuery, astmActions, customRequirement.indexQuery, customRequirementActions, milProfileMeans.indexQuery, milProfileMeansActions, nace.indexQuery, naceActions, props, sspc.indexQuery, sspcActions]);
  let changeContractRequirements = null;
  if (active.contractRequirements && active.contractRequirements.length > 0) {
    changeContractRequirements = active.contractRequirements;
  } else {
    // need to create a default set of contract requirements in there are
    // none do display in the
    changeContractRequirements = [{
      id: 'ContractRequirements_TMP_ID',
      jobID: active.id,
      surfaceProfileUnits: 0
    }];
  }
  return /*#__PURE__*/React.createElement("form", null, /*#__PURE__*/React.createElement("h2", null, "Contract Requirements / Applicable Standards Specified"), /*#__PURE__*/React.createElement("hr", null), changeContractRequirements.map((item, index) => /*#__PURE__*/React.createElement(JobContractRequirementsItem, {
    astmList: astm.list,
    customRequirementList: customRequirement.list,
    data: item,
    index: index,
    key: item.id,
    iso8501List: ISO8501.list,
    naceList: nace.list,
    milProfileMeansList: milProfileMeans.list,
    onRemove: handleRemove,
    onAdd: handleAdd,
    onChange: handleChange,
    sspcList: sspc.list
  })));
};
JobContractRequirementsSubsection.propTypes = {
  active: PropTypes.shape({
    id: PropTypes.string,
    contractRequirements: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  astm: PropTypes.shape({
    indexQuery: PropTypes.shape({}),
    list: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  astmActions: PropTypes.shape({
    serverIndex: PropTypes.func
  }).isRequired,
  customRequirement: PropTypes.shape({
    indexQuery: PropTypes.shape({}),
    list: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  customRequirementActions: PropTypes.shape({
    serverIndex: PropTypes.func
  }).isRequired,
  ISO8501: PropTypes.shape({
    indexQuery: PropTypes.shape({}),
    list: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  ISO8501Actions: PropTypes.shape({
    serverIndex: PropTypes.func
  }).isRequired,
  milProfileMeans: PropTypes.shape({
    indexQuery: PropTypes.shape({}),
    list: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  milProfileMeansActions: PropTypes.shape({
    serverIndex: PropTypes.func
  }).isRequired,
  nace: PropTypes.shape({
    indexQuery: PropTypes.shape({}),
    list: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  naceActions: PropTypes.shape({
    serverIndex: PropTypes.func
  }).isRequired,
  sspc: PropTypes.shape({
    indexQuery: PropTypes.shape({}),
    list: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  sspcActions: PropTypes.shape({
    serverIndex: PropTypes.func
  }).isRequired,
  onJobAdd: PropTypes.func.isRequired,
  onJobRemove: PropTypes.func.isRequired,
  onJobChange: PropTypes.func.isRequired
};
export { JobContractRequirementsSubsection as UncomposedJobContractRequirementsSubsection };
const JobContractRequirements = entityComposition(JobContractRequirementsSubsection, ['astm', 'customRequirement', 'ISO8501', 'milProfileMeans', 'nace', 'sspc']);
export default withJobActions(JobContractRequirements);